import { faBasketShopping } from '@fortawesome/pro-regular-svg-icons'
import {
  ActionIcon,
  Box,
  type BoxProps,
  Button,
  Flex,
  Indicator,
  Menu,
  Skeleton,
  Text,
  Title,
} from '@mantine/core'
import Link from 'next/link'
import { useEffect } from 'react'

import { useTranslation } from '@flecto/translations'
import { Badge, Icon, Result, useMediaQuery } from '@flecto/ui'

import { useStoreBasket } from '../../hooks/useStoreBasket'
import { useStoreDates } from '../../hooks/useStoreDates'
import { useStorePathBuilder } from '../../hooks/useStorePathBuilder'
import { TextCurrencyStore } from '../TextCurrencyStore/TextCurrencyStore'
import { RowItem } from './RowItem'
import styles from './styles.module.css'

export function DropdownBasket(props: BoxProps) {
  const { t } = useTranslation('store', { keyPrefix: 'store-shared' })

  const path = useStorePathBuilder()

  const { isDesktop } = useMediaQuery()

  const { startAt, endAt } = useStoreDates()

  const {
    items,
    totalValue,
    isLoadingTotal,
    opened,
    setBasketOpened,
    recalculateTotalValue,
  } = useStoreBasket()

  useEffect(() => {
    recalculateTotalValue()
  }, [])

  return (
    <Menu
      opened={opened}
      onChange={(value) => setBasketOpened(value)}
      withinPortal
      shadow='md'
    >
      <Menu.Target>
        <ActionIcon
          variant='light'
          color='gray'
          size={isDesktop ? 'md' : 'sm'}
          className={props.className}
        >
          <Indicator label={items.length} disabled={items.length <= 0}>
            <Icon icon={faBasketShopping} />
          </Indicator>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown miw={350} maw={350} className={styles.dropdown}>
        <Box className={styles.items}>
          <Flex align='center' gap='sm'>
            <Title order={5}>{t('dropdown_basket.title')}</Title>
            <Badge size='sm'>
              {t('dropdown_basket.badge_items', { count: items.length })}
            </Badge>
          </Flex>
          {items.map((item, idx) => (
            <RowItem key={`item-basket-${idx}`} item={item.item} />
          ))}
          {items.length <= 0 && (
            <Result mt={-10}>
              <Result.Icon icon={faBasketShopping} />
              <Result.Title>{t('dropdown_basket.empty_title')}</Result.Title>
              <Result.Description>
                {t('dropdown_basket.empty_description')}
              </Result.Description>
            </Result>
          )}
        </Box>

        <Box className={styles.footer}>
          <Flex align='center' justify='space-between' mb='md'>
            <Text fw={500}>{t('dropdown_basket.label_total')}</Text>
            {isLoadingTotal ? (
              <Skeleton h={15} w={100} />
            ) : startAt && endAt ? (
              <TextCurrencyStore cents={totalValue} />
            ) : (
              <Text size='xs'>
                {t('dropdown_basket.label_select_dates_first')}
              </Text>
            )}
          </Flex>
          <Link href={path('/checkout')} shallow={true}>
            <Button fullWidth disabled={items.length <= 0}>
              {t('dropdown_basket.label_button')}
            </Button>
          </Link>
          {!isDesktop && (
            <Button
              fullWidth
              mt='sm'
              h={30}
              variant='white'
              onClick={() => setBasketOpened(false)}
            >
              {t('dropdown_basket.link_close')}
            </Button>
          )}
        </Box>
      </Menu.Dropdown>
    </Menu>
  )
}
